import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { DeliveryRoute, UnassignedPicking } from "app/types/route";
import { DriverOption } from "app/types/driver";
import { MapScreenViewMode, MapSelectionTool, isMapJobSelectable } from "app/types/mapScreen";
import { loadMapScreenData } from "app/utils/mapScreenData";
import { MapScreenToolbar } from "./MapScreenToolbar";
import { MapScreenMap } from "./MapScreenMap";
import { MapScreenSelectionPanel } from "./MapScreenSelectionPanel";
import { MapTimelinePanel } from "./MapTimelinePanel";

type MapScreenProps = {
  accessToken: string;
  routes: DeliveryRoute[];
  unassignedOrders: UnassignedPicking[];
  drivers: DriverOption[];
  userTimezone: string;
  onAssignSelected: (pickingIds: number[]) => void;
  onClose: () => void;
};

export function MapScreen({
  accessToken,
  routes,
  unassignedOrders,
  drivers,
  userTimezone,
  onAssignSelected,
  onClose,
}: MapScreenProps) {
  const [viewMode, setViewMode] = useState<MapScreenViewMode>("select");
  const [selectionTool, setSelectionTool] = useState<MapSelectionTool>("pointer");
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const [highlightedJobId, setHighlightedJobId] = useState<string | null>(null);
  const [focusedRouteId, setFocusedRouteId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [jobs, setJobs] = useState<Awaited<ReturnType<typeof loadMapScreenData>>["jobs"]>([]);
  const [liveDrivers, setLiveDrivers] = useState<
    Awaited<ReturnType<typeof loadMapScreenData>>["liveDrivers"]
  >([]);
  const [timelineRows, setTimelineRows] = useState<
    Awaited<ReturnType<typeof loadMapScreenData>>["timelineRows"]
  >([]);

  const refreshMapData = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    setIsLoading(true);
    try {
      const payload = await loadMapScreenData(
        accessToken,
        routes,
        unassignedOrders,
        drivers
      );
      setJobs(payload.jobs);
      setLiveDrivers(payload.liveDrivers);
      setTimelineRows(payload.timelineRows);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, drivers, routes, unassignedOrders]);

  useEffect(() => {
    void refreshMapData();
  }, [refreshMapData]);

  const selectableJobs = useMemo(
    () => jobs.filter(isMapJobSelectable),
    [jobs]
  );

  const assignedJobs = useMemo(
    () => jobs.filter((job) => !isMapJobSelectable(job)),
    [jobs]
  );

  const selectedJobs = useMemo(
    () => jobs.filter((job) => selectedJobIds.includes(job.id)),
    [jobs, selectedJobIds]
  );

  const selectedPickingIds = useMemo(
    () =>
      jobs
        .filter((job) => selectedJobIds.includes(job.id) && job.pickingId)
        .map((job) => job.pickingId as number),
    [jobs, selectedJobIds]
  );

  const handleToggleJobs = (jobIds: string[]) => {
    const selectableIds = jobIds.filter((jobId) => {
      const job = jobs.find((entry) => entry.id === jobId);
      return job && isMapJobSelectable(job);
    });
    if (selectableIds.length === 0) {
      return;
    }

    const allSelected = selectableIds.every((id) => selectedJobIds.includes(id));
    setSelectedJobIds((current) => {
      if (allSelected) {
        return current.filter((id) => !selectableIds.includes(id));
      }
      return Array.from(new Set([...current, ...selectableIds]));
    });
  };

  const handleSelectJobs = (jobIds: string[]) => {
    const selectableIds = jobIds.filter((jobId) => {
      const job = jobs.find((entry) => entry.id === jobId);
      return job && isMapJobSelectable(job);
    });
    if (selectableIds.length === 0) {
      return;
    }
    setSelectedJobIds((current) =>
      Array.from(new Set([...current, ...selectableIds]))
    );
  };

  const handleRemoveSelected = (jobId: string) => {
    setSelectedJobIds((current) => current.filter((id) => id !== jobId));
  };

  const handleAssignSelected = () => {
    if (selectedPickingIds.length === 0) {
      return;
    }
    onAssignSelected(selectedPickingIds);
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <MapScreenToolbar
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onClose={onClose}
      />

      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
        <MapScreenMap
          accessToken={accessToken}
          active
          viewMode={viewMode}
          jobs={jobs}
          liveDrivers={liveDrivers}
          selectedJobIds={selectedJobIds}
          highlightedJobId={highlightedJobId}
          selectionTool={selectionTool}
          userTimezone={userTimezone}
          onToggleJobs={handleToggleJobs}
          onSelectJobs={handleSelectJobs}
          focusedRouteId={focusedRouteId}
        />

        {viewMode === "select" && (
          <MapScreenSelectionPanel
            jobCount={selectableJobs.length}
            assignedCount={assignedJobs.length}
            selectedJobs={selectedJobs}
            selectionTool={selectionTool}
            userTimezone={userTimezone}
            onSelectionToolChange={setSelectionTool}
            onAssignSelected={handleAssignSelected}
            onClearSelection={() => setSelectedJobIds([])}
            onRemoveSelected={handleRemoveSelected}
            onHighlightJob={setHighlightedJobId}
            canAssign={selectedPickingIds.length > 0}
          />
        )}

        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 600,
              px: 1.25,
              py: 0.75,
              borderRadius: "10px",
              bgcolor: "rgba(255,255,255,0.92)",
              border: "1px solid #e5e9ef",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CircularProgress size={16} sx={{ color: "#64748b" }} />
          </Box>
        )}
      </Box>

      {viewMode === "timeline" && (
        <MapTimelinePanel
          rows={timelineRows}
          onFocusRoute={(routeId) => {
            setFocusedRouteId(routeId);
            setViewMode("live");
          }}
        />
      )}
    </Box>
  );
}
